import React from 'react';
import { cn } from '@/lib/utils';

interface TableContentProps {
  headers: string[];
  dataRows: string[][];
  columnWidths: Record<number, number>;
  onMouseDown?: (e: React.MouseEvent, colIndex: number) => void;
  isResizing?: boolean;
  resizingColumn?: number | null;
}

const TableContent: React.FC<TableContentProps> = ({
  headers,
  dataRows,
  columnWidths,
  onMouseDown,
  isResizing = false,
  resizingColumn = null,
}) => {
  const getMinWidth = (colIndex: number): number => {
    const headerLength = headers[colIndex]?.length || 0;
    const maxDataLength = Math.max(
      ...dataRows.map(row => (row[colIndex] || '').length)
    );
    return Math.max(headerLength * 8, maxDataLength * 8, 80);
  };

  const getColumnWidth = (colIndex: number): number => {
    return columnWidths[colIndex] || getMinWidth(colIndex);
  };

  const processMarkdown = (text: string): React.ReactNode => {
    // Procesar negritas **texto**
    const parts = text.split(/(\*\*.*?\*\*)/);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={idx} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {headers.map((header, colIndex) => (
              <th
                key={colIndex}
                className="border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 relative"
                style={{ width: getColumnWidth(colIndex), minWidth: getMinWidth(colIndex) }}
              >
                {processMarkdown(header.trim())}
                {onMouseDown && (
                  <div
                    className={cn(
                      "absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-emerald-400 transition-colors",
                      isResizing && resizingColumn === colIndex && "bg-emerald-500"
                    )}
                    onMouseDown={(e) => onMouseDown(e, colIndex)}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="hover:bg-gray-50 transition-colors"
            >
              {headers.map((_, colIndex) => (
                <td
                  key={colIndex}
                  className="border border-gray-200 px-3 py-2 text-xs text-gray-700"
                  style={{ width: getColumnWidth(colIndex), minWidth: getMinWidth(colIndex) }}
                >
                  {processMarkdown((row[colIndex] || '').trim())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableContent;


